import { useEffect, useState } from "react";
import Icon from "@/components/base/Icon";

/* The Short / Full switch at the top right of a case study. Used by the NDA
   case studies (ProtectedPageGate, where Full can be locked) and by the
   public ones (CaseVersions).

   First visit: a small note says what the switch does, and the pill pulses
   softly to draw the eye. The note shows once per browser session, on the
   first case study opened. Its text depends on the version that is open: on
   Short it says what Full adds, on Full it says Short is the quicker read.
   It goes away when it is closed, when the switch is used or on the next
   case study. It never takes the focus. The switch points to it with
   aria-describedby, so screen readers hear it with the control.

   Where the note sits: it must never cover the title, and the hero under the
   switch is different on every page. On wide screens it sits on the same row
   as the switch, to its left, where the hero is always empty. On smaller
   screens that row has no room, so the note is a box of its own above the
   page. */
const SEEN_KEY = "view-switch-hint";
/* The value is the path of the first case study opened in this session, then
   "done". This way the note stays on that first page (also after a reload,
   and in React strict mode, which mounts everything twice in development)
   and never shows on the next ones. */
const isFirstLanding = (path: string) => {
  try {
    const seen = sessionStorage.getItem(SEEN_KEY);
    if (seen === null) {
      sessionStorage.setItem(SEEN_KEY, path);
      return true;
    }
    if (seen === path) return true;
    sessionStorage.setItem(SEEN_KEY, "done");
    return false;
  } catch {
    return false; // no storage: better no note than a note on every page
  }
};
const markHintSeen = () => {
  try {
    sessionStorage.setItem(SEEN_KEY, "done");
  } catch {
    /* nothing to do */
  }
};

export default function ViewSwitch({
  view,
  locked,
  hints,
  onChange,
}: {
  view: "short" | "full";
  locked: boolean;
  /** First-visit note for each version, one or two short sentences. Leave a version out for no note there. */
  hints?: { short?: string; full?: string };
  onChange: (v: "short" | "full") => void;
}) {
  // Start each version from the top
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [view]);

  const hint = hints?.[view];
  // decided while rendering, so the page does not jump when the note comes in
  const [showHint, setShowHint] = useState(() => Boolean(hint) && isFirstLanding(window.location.pathname));
  useEffect(() => {
    setShowHint(Boolean(hint) && isFirstLanding(window.location.pathname));
  }, [hint]);
  const wide = useWideScreen();

  const dismiss = () => {
    markHintSeen();
    setShowHint(false);
  };
  const choose = (next: "short" | "full") => {
    dismiss();
    onChange(next);
  };

  const base =
    "btn-toggle toggle-option inline-flex items-center gap-2 type-control whitespace-nowrap";
  const close = (
    <button type="button" onClick={dismiss} aria-label="Close this note" className="btn-icon shrink-0">
      <Icon name="x" aria-hidden="true" />
    </button>
  );
  return (
    <>
      {showHint && !wide && (
        <div role="region" aria-label="Note about this version" className="view-note-bar bg-gray-950">
          <div className="panel flex items-center justify-between gap-4 py-2 pl-5 pr-2">
            <p id="view-switch-hint" className="text-base text-gray-100 leading-snug">
              {hint}
            </p>
            {close}
          </div>
        </div>
      )}

      {/* zero-height anchor: the switch floats over the top right of the page
          that follows. Its place comes from the box tokens (.view-switch in
          index.css), so its right margin equals the left margin of the text. */}
      <div role="region" aria-label="Case study version" className="view-switch-anchor">
        <div className="view-switch">
          {showHint && wide && (
            <div className="relative flex items-center gap-3 max-w-[27rem] rounded-lg border border-gray-600 bg-gray-900 py-2 pl-4 pr-2">
              <p id="view-switch-hint" className="text-base text-gray-100 leading-snug">
                {hint}
              </p>
              {close}
              {/* arrow that points at the switch */}
              <span
                className="absolute top-1/2 -right-1 -translate-y-1/2 w-2 h-2 rotate-45 bg-gray-900 border-r border-t border-gray-600"
                aria-hidden="true"
              />
            </div>
          )}
          <div
            role="group"
            aria-label="Case study length"
            aria-describedby={showHint ? "view-switch-hint" : undefined}
            className={`toggle-track inline-flex items-center ${showHint ? "switch-hint" : ""}`}
          >
            <button
              type="button"
              aria-pressed={view === "short"}
              onClick={() => choose("short")}
              className={base}
            >
              Short
            </button>
            <button
              type="button"
              aria-pressed={view === "full"}
              onClick={() => choose("full")}
              className={base}
            >
              {locked && <Icon name="lock" aria-hidden="true" />}
              Full
              {locked && <span className="sr-only"> (needs the password)</span>}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

/** True from the "lg" breakpoint up, where the row of the switch has room for the note. */
function useWideScreen() {
  const query = "(min-width: 1024px)";
  const [wide, setWide] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const media = window.matchMedia(query);
    const update = () => setWide(media.matches);
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);
  return wide;
}
